<div
  class="rq"
  (dragenter)="onDragEnter($event)"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave()"
  (drop)="onDrop($event)"
>
  <div class="rq-drop" *ngIf="dragging()">
    <mat-icon>upload_file</mat-icon>
    {{ "support_chat.drop_files" | translate }}
  </div>

  <!-- ================================ Header ================================ -->
  <header class="rq-header">
    <div class="rq-title-row">
      <span class="rq-header-icon">
        <ng-container *ngTemplateOutlet="ticketGlyph; context: { size: 22, stroke: 1.8 }"></ng-container>
      </span>
      <div class="rq-title-text">
        <h2 mat-dialog-title>{{ "support_requests.title" | translate }}</h2>
        <p>{{ "support_requests.subtitle" | translate }}</p>
      </div>
      <button
        type="button"
        class="rq-close"
        (click)="requestClose()"
        [disabled]="sending()"
        [attr.aria-label]="'support_chat.close' | translate"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18"></path>
        </svg>
      </button>
    </div>

    <nav class="rq-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="rq-tab"
        [class.rq-tab--on]="tab() === 'new'"
        [attr.aria-selected]="tab() === 'new'"
        [disabled]="sending()"
        (click)="goNew()"
      >
        {{ "support_requests.tab_new" | translate }}
      </button>
      <button
        type="button"
        role="tab"
        class="rq-tab"
        [class.rq-tab--on]="tab() === 'mine'"
        [attr.aria-selected]="tab() === 'mine'"
        [disabled]="sending()"
        (click)="goMine()"
      >
        {{ "support_requests.tab_mine" | translate }}
        <span class="rq-count" *ngIf="requests.openCount()">{{ requests.openCount() }}</span>
      </button>
    </nav>
  </header>

  <!-- ======================== New request: pick a type ======================= -->
  <ng-container *ngIf="tab() === 'new' && step() === 'type'">
    <div class="rq-body rq-types">
      <p class="rq-heading">{{ "support_requests.pick_heading" | translate }}</p>
      <section class="rq-group" *ngFor="let group of groups">
        <span class="rq-group-label">{{ "support_requests.groups." + group | translate }}</span>
        <div class="rq-tiles">
          <button
            *ngFor="let def of typesIn(group)"
            type="button"
            class="rq-tile"
            (click)="pick(def)"
          >
            <span class="rq-tile-icon" [attr.data-tone]="def.tone || null">
              <ng-container *ngTemplateOutlet="typeGlyph; context: { icon: def.icon }"></ng-container>
            </span>
            <span class="rq-tile-text">
              <span class="rq-tile-title">{{ typeKey("title", def.type) | translate }}</span>
              <span class="rq-tile-hint">{{ typeKey("hint", def.type) | translate }}</span>
            </span>
          </button>
        </div>
      </section>
    </div>
    <footer class="rq-footer rq-footer--end">
      <button type="button" class="rq-btn rq-btn--ghost" (click)="requestClose()">
        {{ "support_requests.cancel" | translate }}
      </button>
    </footer>
  </ng-container>

  <!-- ========================== New request: the form ========================= -->
  <ng-container *ngIf="tab() === 'new' && step() === 'form' && def() as def">
    <div class="rq-form-head">
      <button
        type="button"
        class="rq-back"
        (click)="backToTypes()"
        [disabled]="sending()"
        [attr.aria-label]="'support_requests.back_to_types' | translate"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M15 5l-7 7 7 7"></path>
        </svg>
      </button>
      <div class="rq-form-title">
        <span>{{ typeKey("form") | translate }}</span>
        <small>{{ typeKey("sub") | translate }}</small>
      </div>
    </div>

    <div class="rq-body rq-form" [class.rq-form--busy]="sending()">
      <!-- Which nudges? (broken, toggle, change) -->
      <app-request-nudge-picker
        *ngIf="def.nudges"
        [nudges]="nudges()"
        [state]="nudgesState()"
        [(selected)]="form.nudgeIds"
        [hintKey]="def.nudges === 'optional' ? 'support_requests.optional' : 'support_requests.nudges_hint_named'"
        [disabled]="sending()"
        (retry)="loadNudges()"
      ></app-request-nudge-picker>

      <!-- Toggle: pause or turn on -->
      <div class="rq-field" *ngIf="def.type === 'toggle'">
        <span class="rq-label" id="rq-action-label">{{ "support_requests.action_label" | translate }}</span>
        <div class="rq-chips" role="radiogroup" aria-labelledby="rq-action-label">
          <button
            *ngFor="let action of ['pause', 'on']"
            type="button"
            role="radio"
            class="rq-chip rq-chip--wide"
            [class.rq-chip--on]="form.action === action"
            [attr.aria-checked]="form.action === action"
            (click)="form.action = $any(action)"
          >
            {{ "support_requests.action_" + action | translate }}
          </button>
        </div>
      </div>

      <!-- Change: what should change -->
      <div class="rq-field" *ngIf="def.type === 'change'">
        <span class="rq-label" id="rq-change-label">
          {{ "support_requests.change_label" | translate }}
          <span class="rq-hint">{{ "support_requests.pick_all" | translate }}</span>
        </span>
        <div class="rq-chips" role="group" aria-labelledby="rq-change-label">
          <button
            *ngFor="let option of changeOptions"
            type="button"
            class="rq-chip"
            [class.rq-chip--on]="isIn(form.changes, option)"
            [attr.aria-pressed]="isIn(form.changes, option)"
            (click)="form.changes = toggleIn(form.changes, option)"
          >
            {{ "support_requests.changes." + option | translate }}
          </button>
        </div>
      </div>

      <!-- Pages (idea) -->
      <div class="rq-field" *ngIf="def.pages">
        <span class="rq-label" id="rq-pages-label">
          {{ "support_requests.pages_label" | translate }}
          <span class="rq-hint">{{ "support_requests.optional" | translate }}</span>
        </span>
        <div class="rq-chips" role="group" aria-labelledby="rq-pages-label">
          <button
            *ngFor="let option of pageOptions"
            type="button"
            class="rq-chip"
            [class.rq-chip--on]="isIn(form.pages, option)"
            [attr.aria-pressed]="isIn(form.pages, option)"
            (click)="form.pages = toggleIn(form.pages, option)"
          >
            {{ "support_requests.pages." + option | translate }}
          </button>
        </div>
      </div>

      <!-- Broken: link to the page -->
      <ng-container *ngIf="def.type === 'broken'">
        <div class="rq-field">
          <label class="rq-label" for="rq-url">
            {{ "support_requests.url_label" | translate }}
            <span class="rq-hint">{{ "support_requests.optional" | translate }}</span>
          </label>
          <input
            id="rq-url"
            class="rq-input"
            type="url"
            placeholder="https://"
            [class.rq-input--invalid]="urlInvalid"
            [(ngModel)]="form.url"
          />
          <span class="rq-field-error" *ngIf="urlInvalid" role="alert">{{ "support_requests.url_invalid" | translate }}</span>
        </div>
      </ng-container>

      <!-- Toggle: starting / until -->
      <div class="rq-row rq-row--wide-gap" *ngIf="def.type === 'toggle'">
        <div class="rq-field">
          <span class="rq-label" id="rq-when-label">{{ "support_requests.starting" | translate }}</span>
          <div class="rq-chips" role="radiogroup" aria-labelledby="rq-when-label">
            <button
              *ngFor="let when of ['now', 'date']"
              type="button"
              role="radio"
              class="rq-chip"
              [class.rq-chip--on]="form.when === when"
              [attr.aria-checked]="form.when === when"
              (click)="form.when = $any(when)"
            >
              {{ (when === "now" ? "support_requests.when_now" : "support_requests.pick_date") | translate }}
            </button>
          </div>
          <ng-container *ngIf="form.when === 'date'">
            <label class="rq-sublabel" for="rq-when-day">{{ "support_requests.start_date" | translate }}</label>
            <div class="rq-day-time">
              <div class="rq-day">
                <input
                  id="rq-when-day"
                  class="rq-input"
                  [matDatepicker]="whenPicker"
                  [(ngModel)]="form.whenDay"
                  [min]="today"
                  [placeholder]="'support_requests.pick_day' | translate"
                  [attr.aria-label]="'support_requests.start_date' | translate"
                  readonly
                  (click)="whenPicker.open()"
                />
                <mat-datepicker-toggle [for]="whenPicker"></mat-datepicker-toggle>
                <mat-datepicker #whenPicker></mat-datepicker>
              </div>
              <mat-form-field class="bhm-option-container-1 medium-height-container rq-time">
                <mat-select
                  [(ngModel)]="form.whenTime"
                  [disabled]="!form.whenDay"
                  [attr.aria-label]="'support_requests.time' | translate"
                >
                  <mat-option *ngFor="let time of timeOptions" class="bhm-option-select-option-1" [value]="time">{{ time }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </ng-container>
        </div>
        <div class="rq-field">
          <span class="rq-label" id="rq-until-label">{{ "support_requests.until" | translate }}</span>
          <div class="rq-chips" role="radiogroup" aria-labelledby="rq-until-label">
            <button
              *ngFor="let until of ['manual', 'date']"
              type="button"
              role="radio"
              class="rq-chip"
              [class.rq-chip--on]="form.until === until"
              [attr.aria-checked]="form.until === until"
              (click)="form.until = $any(until)"
            >
              {{ (until === "manual" ? "support_requests.until_manual" : "support_requests.pick_date") | translate }}
            </button>
          </div>
          <ng-container *ngIf="form.until === 'date'">
            <label class="rq-sublabel" for="rq-until-day">{{ "support_requests.end_date" | translate }}</label>
            <div class="rq-day-time">
              <div class="rq-day">
                <input
                  id="rq-until-day"
                  class="rq-input"
                  [matDatepicker]="untilPicker"
                  [(ngModel)]="form.untilDay"
                  [min]="form.whenDay || today"
                  [placeholder]="'support_requests.pick_day' | translate"
                  [attr.aria-label]="'support_requests.end_date' | translate"
                  readonly
                  (click)="untilPicker.open()"
                />
                <mat-datepicker-toggle [for]="untilPicker"></mat-datepicker-toggle>
                <mat-datepicker #untilPicker></mat-datepicker>
              </div>
              <mat-form-field class="bhm-option-container-1 medium-height-container rq-time">
                <mat-select
                  [(ngModel)]="form.untilTime"
                  [disabled]="!form.untilDay"
                  [attr.aria-label]="'support_requests.time' | translate"
                >
                  <mat-option *ngFor="let time of timeOptions" class="bhm-option-select-option-1" [value]="time">{{ time }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </ng-container>
        </div>
        <span class="rq-field-error rq-row-error" *ngIf="scheduleError as key" role="alert">{{ key | translate }}</span>
      </div>

      <!-- Data: product, period, attach the view -->
      <ng-container *ngIf="def.type === 'data'">
        <div class="rq-row">
          <div class="rq-field">
            <span class="rq-label" id="rq-product-label">{{ "support_requests.product" | translate }}</span>
            <mat-form-field class="bhm-option-container-1 medium-height-container">
              <mat-select
                [(ngModel)]="form.product"
                aria-labelledby="rq-product-label"
                [placeholder]="'support_requests.product_placeholder' | translate"
              >
                <mat-option *ngFor="let option of products" class="bhm-option-select-option-1" [value]="option.key">
                  {{ option.titleKey | translate }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="rq-field">
            <span class="rq-label" id="rq-period-label">{{ "support_requests.period" | translate }}</span>
            <!-- One field, two days: either half opens the range picker. -->
            <div
              class="rq-range"
              role="group"
              aria-labelledby="rq-period-label"
              [class.rq-input--invalid]="rangeInvalid(form.periodFrom, form.periodTo)"
            >
              <mat-date-range-input [rangePicker]="periodPicker" (click)="periodPicker.open()">
                <input
                  matStartDate
                  readonly
                  [(ngModel)]="form.periodFrom"
                  [placeholder]="'support_requests.from' | translate"
                  [attr.aria-label]="'support_requests.from' | translate"
                />
                <input
                  matEndDate
                  readonly
                  [(ngModel)]="form.periodTo"
                  [placeholder]="'support_requests.to' | translate"
                  [attr.aria-label]="'support_requests.to' | translate"
                />
              </mat-date-range-input>
              <mat-datepicker-toggle [for]="periodPicker"></mat-datepicker-toggle>
              <mat-date-range-picker #periodPicker></mat-date-range-picker>
            </div>
          </div>
        </div>
        <span class="rq-field-error" *ngIf="rangeInvalid(form.periodFrom, form.periodTo)" role="alert">{{ "support_requests.range_invalid" | translate }}</span>
        <label class="rq-check">
          <input type="checkbox" [(ngModel)]="form.attachView" />
          <span>
            <strong>{{ "support_requests.attach_view" | translate }}</strong>
            <small>{{ "support_requests.attach_view_hint" | translate }}</small>
          </span>
        </label>
      </ng-container>

      <!-- Access: who, and what they can see -->
      <ng-container *ngIf="def.type === 'access'">
        <div class="rq-row">
          <div class="rq-field">
            <label class="rq-label" for="rq-name">{{ "support_requests.name" | translate }}</label>
            <input id="rq-name" class="rq-input" type="text" autocomplete="off" [(ngModel)]="form.name" />
          </div>
          <div class="rq-field">
            <label class="rq-label" for="rq-email">{{ "support_requests.email" | translate }}</label>
            <input
              id="rq-email"
              class="rq-input"
              type="email"
              autocomplete="off"
              [class.rq-input--invalid]="emailInvalid"
              [(ngModel)]="form.email"
            />
            <span class="rq-field-error" *ngIf="emailInvalid" role="alert">{{ "support_requests.email_invalid" | translate }}</span>
          </div>
        </div>
        <div class="rq-field" *ngIf="accessOptions.length">
          <span class="rq-label" id="rq-products-label">{{ "support_requests.products_label" | translate }}</span>
          <div class="rq-chips" role="group" aria-labelledby="rq-products-label">
            <button
              *ngFor="let option of accessOptions"
              type="button"
              class="rq-chip"
              [class.rq-chip--on]="isIn(form.accessProducts, option)"
              [attr.aria-pressed]="isIn(form.accessProducts, option)"
              (click)="form.accessProducts = toggleIn(form.accessProducts, option)"
            >
              {{ "sidenav_links." + option | translate }}
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Heads-up: what, from, to -->
      <ng-container *ngIf="def.type === 'headsup'">
        <div class="rq-field">
          <span class="rq-label" id="rq-events-label">{{ "support_requests.events_label" | translate }}</span>
          <div class="rq-chips" role="group" aria-labelledby="rq-events-label">
            <button
              *ngFor="let option of eventOptions"
              type="button"
              class="rq-chip"
              [class.rq-chip--on]="isIn(form.events, option)"
              [attr.aria-pressed]="isIn(form.events, option)"
              (click)="form.events = toggleIn(form.events, option)"
            >
              {{ "support_requests.events." + option | translate }}
            </button>
          </div>
        </div>
        <div class="rq-row">
          <div class="rq-field">
            <label class="rq-label" for="rq-from">{{ "support_requests.from" | translate }}</label>
            <div class="rq-day">
              <input
                id="rq-from"
                class="rq-input"
                [matDatepicker]="fromPicker"
                [(ngModel)]="form.from"
                  [max]="form.to"
                [placeholder]="'support_requests.pick_day' | translate"
                readonly
                (click)="fromPicker.open()"
              />
              <mat-datepicker-toggle [for]="fromPicker"></mat-datepicker-toggle>
              <mat-datepicker #fromPicker></mat-datepicker>
            </div>
          </div>
          <div class="rq-field">
            <label class="rq-label" for="rq-to">{{ "support_requests.to" | translate }}</label>
            <div class="rq-day">
              <input
                id="rq-to"
                class="rq-input"
                [matDatepicker]="toPicker"
                [(ngModel)]="form.to"
                  [min]="form.from"
                  [class.rq-input--invalid]="rangeInvalid(form.from, form.to)"
                [placeholder]="'support_requests.pick_day' | translate"
                readonly
                (click)="toPicker.open()"
              />
              <mat-datepicker-toggle [for]="toPicker"></mat-datepicker-toggle>
              <mat-datepicker #toPicker></mat-datepicker>
            </div>
          </div>
        </div>
        <span class="rq-field-error" *ngIf="rangeInvalid(form.from, form.to)" role="alert">{{ "support_requests.range_invalid" | translate }}</span>
      </ng-container>

      <!-- Message (every type), with files -->
      <div class="rq-field">
        <label class="rq-label" for="rq-message">
          {{ typeKey("desc") | translate }}
          <span class="rq-hint" *ngIf="!def.messageRequired">{{ "support_requests.optional" | translate }}</span>
        </label>
        <div class="rq-box">
          <textarea
            id="rq-message"
            cdkTextareaAutosize
            cdkAutosizeMinRows="4"
            cdkAutosizeMaxRows="12"
            [(ngModel)]="form.message"
            [maxlength]="maxLength"
            [placeholder]="typeKey('ph') | translate"
            (paste)="onPaste($event)"
          ></textarea>
          <div class="rq-box-actions" *ngIf="canAttach">
            <span>{{ "support_requests.files_hint" | translate: { max: maxFileMb } }}</span>
            <button type="button" class="rq-attach" (click)="pickFiles()">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M20.5 11.5l-8.3 8.3a5 5 0 0 1-7.1-7.1l8.6-8.6a3.3 3.3 0 0 1 4.7 4.7l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9"></path>
              </svg>
              {{ "support_requests.attach" | translate }}
            </button>
          </div>
        </div>
        <input
          #filePicker
          type="file"
          class="rq-file-picker"
          multiple
          [accept]="fileAccept"
          (change)="onFilesPicked(filePicker)"
          tabindex="-1"
          aria-hidden="true"
        />
        <ul class="rq-tray" *ngIf="files.count()">
          <li *ngFor="let item of files.items()" class="rq-tray-item" [class.is-failed]="item.status === 'failed'">
            <img
              *ngIf="item.previewUrl; else trayIcon"
              class="rq-tray-thumb"
              [src]="item.previewUrl"
              alt=""
              (error)="files.dropPreview(item.localId)"
            />
            <ng-template #trayIcon>
              <mat-icon class="rq-tray-icon">{{ fileIcon(item.file.type) }}</mat-icon>
            </ng-template>
            <span class="rq-tray-text">
              <strong [title]="item.file.name">{{ item.file.name }}</strong>
              <small *ngIf="item.status === 'failed'" class="rq-error-text">
                {{ uploadFailureKey(item) | translate }}
                <button type="button" class="rq-link" (click)="files.retry(item.localId)">
                  {{ "support_chat.retry" | translate }}
                </button>
              </small>
              <small *ngIf="item.status === 'uploading'">{{ item.progress * 100 | number: "1.0-0" }}%</small>
              <small *ngIf="item.status === 'done'">{{ fileSize(item.file.size) }}</small>
            </span>
            <button
              type="button"
              class="rq-tray-remove"
              (click)="removeFile(item.localId)"
              [disabled]="sending()"
              [attr.aria-label]="'support_chat.remove_file' | translate: { name: item.file.name }"
            >
              <mat-icon>close</mat-icon>
            </button>
            <span class="rq-tray-progress" *ngIf="item.status === 'uploading'" [style.width.%]="item.progress * 100"></span>
          </li>
        </ul>
        <p class="rq-notice" *ngIf="attachmentNotice() as notice" role="alert">
          {{ notice.key | translate: notice.params }}
        </p>
      </div>

      <p class="rq-error" *ngIf="errorKey() as key" role="alert">
        <mat-icon>error_outline</mat-icon>
        <span>{{ key | translate }}</span>
      </p>
    </div>

    <footer class="rq-footer">
      <button type="button" class="rq-text-btn" (click)="backToTypes()" [disabled]="sending()">
        {{ "support_requests.change_type" | translate }}
      </button>
      <div class="rq-footer-actions">
        <button type="button" class="rq-btn rq-btn--ghost" (click)="requestClose()" [disabled]="sending()">
          {{ "support_requests.cancel" | translate }}
        </button>
        <button type="button" class="rq-btn" (click)="send()" [disabled]="!canSend">
          <span class="rq-spinner" *ngIf="sending()" aria-hidden="true"></span>
          {{ (sending() ? "support_requests.sending" : "support_requests.send") | translate }}
        </button>
      </div>
    </footer>
  </ng-container>

  <!-- ============================ New request: sent =========================== -->
  <ng-container *ngIf="tab() === 'new' && step() === 'done' && sent() as done">
    <div class="rq-sent">
      <span class="rq-sent-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5"></path>
        </svg>
      </span>
      <h3>{{ "support_requests.sent_title" | translate }}</h3>
      <p [innerHTML]="sentText(done.id)"></p>
    </div>
    <ol class="rq-tracker">
      <li class="rq-tracker-step rq-tracker-step--on">
        <span class="rq-tracker-bar"></span>
        <strong>{{ "support_requests.status.received" | translate }}</strong>
        <small>{{ "support_requests.tracker_received" | translate }}</small>
      </li>
      <li class="rq-tracker-step">
        <span class="rq-tracker-bar"></span>
        <strong>{{ "support_requests.status.in_progress" | translate }}</strong>
        <small>{{ "support_requests.tracker_in_progress" | translate }}</small>
      </li>
      <li class="rq-tracker-step">
        <span class="rq-tracker-bar"></span>
        <strong>{{ "support_requests.status.done" | translate }}</strong>
        <small>{{ "support_requests.tracker_done" | translate }}</small>
      </li>
    </ol>
    <footer class="rq-footer rq-footer--center">
      <button type="button" class="rq-btn rq-btn--ghost" (click)="startOver()">
        {{ "support_requests.send_another" | translate }}
      </button>
      <button type="button" class="rq-btn" (click)="viewMine()">
        {{ "support_requests.view_mine" | translate }}
      </button>
    </footer>
  </ng-container>

  <!-- ================================ My requests ============================== -->
  <ng-container *ngIf="tab() === 'mine'">
    <div class="rq-body rq-mine">
      <div class="rq-filters" role="radiogroup" [attr.aria-label]="'support_requests.tab_mine' | translate">
        <button
          *ngFor="let option of ['open', 'done', 'all']"
          type="button"
          role="radio"
          class="rq-filter"
          [class.rq-filter--on]="filter() === option"
          [attr.aria-checked]="filter() === option"
          (click)="setFilter($any(option))"
        >
          {{ "support_requests.filter_" + option | translate: { count: requests.openCount() } }}
        </button>
      </div>

      <p class="rq-muted" *ngIf="mineState() === 'loading'" role="status">
        {{ "support_requests.mine_loading" | translate }}
      </p>
      <p class="rq-muted" *ngIf="mineState() === 'error'" role="alert">
        {{ "support_requests.mine_error" | translate }}
        <button type="button" class="rq-link" (click)="loadMine()">{{ "support_chat.retry" | translate }}</button>
      </p>
      <p class="rq-empty" *ngIf="mineState() === 'ready' && !mine().length">
        {{ "support_requests.mine_empty_" + filter() | translate }}
      </p>

      <ul class="rq-list" *ngIf="mineState() === 'ready' && mine().length">
        <li *ngFor="let request of mine(); trackBy: trackRequest" class="rq-item">
          <span class="rq-item-text">
            <strong>{{ request.title || (typeKey("title", request.type) | translate) }}</strong>
            <small>{{ meta(request) }}</small>
          </span>
          <span class="rq-status" [attr.data-status]="request.status">
            {{ "support_requests.status." + request.status | translate }}
          </span>
        </li>
      </ul>
    </div>
    <footer class="rq-footer rq-footer--end">
      <button type="button" class="rq-btn" (click)="goNew()">
        {{ "support_requests.tab_new" | translate }}
      </button>
    </footer>
  </ng-container>
</div>

<!-- ================================== Glyphs ================================= -->
<ng-template #ticketGlyph let-size="size" let-stroke="stroke">
  <svg [attr.width]="size" [attr.height]="size" viewBox="0 0 24 24" fill="none" stroke="currentColor" [attr.stroke-width]="stroke" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M4 6h16v4a2 2 0 0 0 0 4v4H4v-4a2 2 0 0 0 0-4z"></path>
    <path d="M14.5 6v12" stroke-dasharray="2 2.2"></path>
  </svg>
</ng-template>

<ng-template #typeGlyph let-icon="icon">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" [ngSwitch]="icon">
    <g *ngSwitchCase="'toggle'">
      <rect x="2.5" y="7" width="19" height="10" rx="5"></rect>
      <circle cx="8" cy="12" r="2.6"></circle>
    </g>
    <g *ngSwitchCase="'pencil'">
      <path d="M4 20h4L19 9l-4-4L4 16v4z"></path>
      <path d="M13 7l4 4"></path>
    </g>
    <g *ngSwitchCase="'warning'">
      <path d="M12 3.5 2.5 20h19L12 3.5z"></path>
      <path d="M12 10v4"></path>
      <path d="M12 17h.01"></path>
    </g>
    <g *ngSwitchCase="'chart'">
      <path d="M4 20V4"></path>
      <path d="M4 20h16"></path>
      <path d="M8.5 16v-5"></path>
      <path d="M13 16V8"></path>
      <path d="M17.5 16v-3"></path>
    </g>
    <g *ngSwitchCase="'bulb'">
      <path d="M9 18h6"></path>
      <path d="M10 21h4"></path>
      <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z"></path>
    </g>
    <g *ngSwitchCase="'calendar'">
      <rect x="3" y="5" width="18" height="16" rx="2"></rect>
      <path d="M3 10h18"></path>
      <path d="M8 3v4"></path>
      <path d="M16 3v4"></path>
    </g>
    <g *ngSwitchCase="'person-add'">
      <circle cx="9" cy="8" r="3.6"></circle>
      <path d="M2.5 20.5c0-3.7 2.9-5.8 6.5-5.8s6.5 2.1 6.5 5.8"></path>
      <path d="M19 8v6"></path>
      <path d="M16 11h6"></path>
    </g>
    <g *ngSwitchCase="'question'">
      <circle cx="12" cy="12" r="9"></circle>
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14"></path>
      <path d="M12 17h.01"></path>
    </g>
  </svg>
</ng-template>
